import { Injectable, Injector } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { REDIRECTPAGE } from '@domains/shared/domain/utils/global-constants';
import { SELF_HANDLED_STATUSES, serverMessage } from '@domains/shared/domain/utils/server-error';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslateService } from '@ngx-translate/core';

/**
 * Obrazovky, které si chybu hlásí samy lepší větou, než jakou pošle server.
 *
 * Přihlášení odpoví 400, ale uživateli patří „nesprávné jméno nebo heslo", ne
 * serverová hláška; aktivační a resetovací odkaz zas mají vlastní text o propadlé
 * platnosti. Bez téhle výjimky by se hlášky zdvojily. Porovnává se kus URL, stejně
 * jako to dělá token interceptor.
 *
 * Nastavení hesla z odkazu (`new-password`, `activate-account`) si hlásí chybu samo
 * od EL-2286 — propadlý odkaz ukáže stránka místo formuláře, a „Neplatný token." ze
 * serveru by k tomu byl jen matoucí dodatek.
 */
const SELF_HANDLED_URLS = ['login', 'checktoken', 'reset-password', 'forgotten-password', 'new-password', 'activate-account'];

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

  constructor(public notify: NzMessageService, private router: Router, private injector: Injector) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((response: HttpErrorResponse) => {
        this.report(request, response);

        return throwError(() => response);
      })
    );
  }

  /**
   * Jediné místo, kde se chyba dostane k uživateli.
   *
   * Dřív se hlásila jen 500 a 401 — jenže backend vrací u odmítnutí 400 nebo 404
   * s připravenou lokalizovanou větou a ta se nikam nedostala. Kdo si ji nechytil sám,
   * mlčel: tlačítko nereagovalo, stránka zůstala prázdná. Proto se teď ohlásí každá
   * chyba a do konzole jde záznam ke každé, ať je co dohledat.
   *
   * ⚠ Komponenta proto v `error:` větvi **vlastní hlášku nepřidává** — jen uklidí stav
   * (spinner, dialog, přepínač). Jinak má uživatel dvě hlášky, často stejnou větu
   * dvakrát (EL-2297, EL-2307). Větev ale nechá, i prázdnou: bez ní RxJS chybu vyhodí
   * jako neošetřenou. Výjimky: `SELF_HANDLED_URLS` a `SELF_HANDLED_STATUSES`.
   */
  private report(request: HttpRequest<any>, response: HttpErrorResponse): void {
    console.error(`HTTP ${response.status} ${request.method} ${request.url}`, response.error);

    if (response.status === 401) {
      this.notify.error(this.translate('core.errors.sessionExpired'));
      // S adresou, kde uživatel byl — přihlášení ho tam po novém přihlášení vrátí
      // (jen v sekci jeho role, `safeReturnUrl`). Student tak skončí zpátky v testu,
      // ne na nástěnce (EL-2290 A). Z obrazovek přihlášení se nikam nevrací.
      const returnUrl = this.router.url;
      this.router.navigate(
        [REDIRECTPAGE.LOGIN_PAGE],
        returnUrl.startsWith('/auth') ? {} : { queryParams: { returnUrl } },
      );

      return;
    }

    if (this.isSelfHandled(request, response)) {
      return;
    }

    // Bez odpovědi serveru (offline, spadlé spojení) — status 0 dosud nehlásil nic.
    if (response.status === 0) {
      this.notify.error(this.translate('core.errors.connection'));

      return;
    }

    // Serverovou hlášku z 500 uživateli neukazujeme, může nést vnitřnosti aplikace.
    if (response.status >= 500) {
      this.notify.error(this.translate('core.errors.serverError'));

      return;
    }

    // 4xx: server umí říct proč a jeho věta je už v jazyce uživatele.
    this.notify.error(serverMessage(response) ?? this.translate('core.errors.requestFailed'));
  }

  /** Celá obrazovka ze `SELF_HANDLED_URLS`, nebo jen stav, který si request vyhradil. */
  private isSelfHandled(request: HttpRequest<any>, response: HttpErrorResponse): boolean {
    const url = request.url.toLowerCase();

    return SELF_HANDLED_URLS.some((fragment) => url.includes(fragment))
      || request.context.get(SELF_HANDLED_STATUSES).includes(response.status);
  }

  /**
   * TranslateService se resolvuje až tady, ne v konstruktoru — první HTTP request
   * v aplikaci je stažení překladů a v konstruktoru by se DI zacyklilo.
   */
  private translate(key: string): string {
    return this.injector.get(TranslateService).instant(key);
  }
}
